import { Injectable } from '@angular/core';
import { Product } from '../../../modals/product.modals';

/**
 * Mobile industry only: several cart rows can point at the exact same
 * physical stock (same product + same batch/variant `productmapvalues`
 * row) while being tagged with different "mappings" (e.g. a yellow S2
 * case and a yellow A36 case cut from the same batch of 6). The API
 * reports the full batch qty on every one of those rows independently,
 * so without pooling, each row could be sold up to the full qty and the
 * batch would oversell.
 *
 * The live cart (`products`) is the source of truth for how much of a
 * pool is already committed - it self-corrects on every add/increase/
 * decrease/delete with no separate counters to keep in sync. localStorage
 * is only a write-through mirror of that snapshot (per client requirement),
 * never read back as authoritative.
 */
@Injectable({
  providedIn: 'root',
})
export class MobileStockReservationService {
  private readonly STORAGE_KEY = 'mobile_variant_stock_reservations';

  /**
   * Identifies the shared physical stock pool a cart row draws from:
   * same product + same productmapvalues row (batch+variant), regardless
   * of which mapping (S2, A36, ...) the row is tagged with.
   */
  buildPoolKey(psid: string | undefined | null, mapValueId: number | string | undefined | null): string {
    return `${(psid || '').toLowerCase()}::${mapValueId ?? ''}`;
  }

  /**
   * Sum of increaseQuantity across every cart row sharing this pool.
   * Pass `excludeProducts` to leave out the row(s) currently being edited
   * (e.g. when computing how much MORE a row can grow, or when merging
   * two rows into one).
   */
  getReservedQty(poolKey: string, products: Product[], excludeProducts?: Product[]): number {
    return products.reduce((sum, item) => {
      if (excludeProducts?.includes(item)) return sum;
      if (!item.selectedMapValue) return sum;
      if (this.buildPoolKey(item.psid, item.selectedMapValue.id) !== poolKey) return sum;
      return sum + (Number(item.increaseQuantity) || 0);
    }, 0);
  }

  /**
   * Remaining stock in the pool after subtracting what other rows have
   * already committed. `baseQty` is the qty reported on the selected
   * productmapvalues row (the pool's total physical stock).
   */
  getAvailableQty(
    poolKey: string,
    baseQty: number,
    products: Product[],
    excludeProducts?: Product[],
  ): number {
    const reserved = this.getReservedQty(poolKey, products, excludeProducts);
    console.log(`Reserved quantity for poolKey "${poolKey}":`, reserved);
    return Math.max(0, (Number(baseQty) || 0) - reserved);
  }

  /**
   * Write-through mirror of the current per-pool reservation snapshot.
   * Not used for cap decisions - the live cart array always is.
   */
  syncToStorage(products: Product[]): void {
    try {
      const snapshot: Record<string, number> = {};
      for (const item of products) {
        if (!item.selectedMapValue) continue;
        const key = this.buildPoolKey(item.psid, item.selectedMapValue.id);
        snapshot[key] = (snapshot[key] || 0) + (Number(item.increaseQuantity) || 0);
      }
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // localStorage unavailable/full - non-fatal, pool checks still work
      // off the live cart array.
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
